import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import SEO from '../components/SEO';
import { HERO_MEDIA, OFFICES, SALES_TEAM, SITE } from '../config/media';
import { submitContact } from '../lib/api';
import { usePageContent } from '../lib/useContent';
import { orgJsonLd, contactPageJsonLd, breadcrumbJsonLd, personJsonLd } from '../lib/seo';

const CONTACT_HERO_FALLBACK = {
    hero: {
        eyebrow: 'Contact',
        headline: 'Talk directly to the manufacturer.',
        body: 'No middlemen. Pricing, lead times and delivery come straight from the mill floor.',
        image_url: HERO_MEDIA.cranePad.src,
    },
};

const TOPICS = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'sales', label: 'Sales / Quote' },
    { value: 'partnership', label: 'Indigenous Partnership' },
    { value: 'careers', label: 'Careers' },
    { value: 'other', label: 'Other' },
];

export default function Contact() {
    const content = usePageContent('contact', CONTACT_HERO_FALLBACK);
    const hero = content.hero || CONTACT_HERO_FALLBACK.hero;
    const [params] = useSearchParams();
    const initialTopic = TOPICS.some((t) => t.value === params.get('topic'))
        ? params.get('topic')
        : 'general';

    const [form, setForm] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        topic: initialTopic,
        message: '',
        website: '', // honeypot
    });
    const [status, setStatus] = useState('idle'); // idle | submitting | success | error
    const [error, setError] = useState('');

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setError('');
        try {
            const payload = { ...form };
            // Strip empty optional strings
            ['company', 'phone'].forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            await submitContact(payload);
            setStatus('success');
        } catch (err) {
            setStatus('error');
            setError(
                err?.response?.data?.detail
                    ? typeof err.response.data.detail === 'string'
                        ? err.response.data.detail
                        : 'Please check the highlighted fields.'
                    : 'Something went wrong. Please try again.'
            );
        }
    };

    return (
        <>
            <SEO
                title="Contact - Talk Directly to the Manufacturer"
                description="Reach the Centurion sales team directly. Offices in Chemainus, BC and Calgary, AB - Manufacturer Direct ground protection across Western Canada."
                cmsSlug="seo.contact"
                path="/contact"
                image={HERO_MEDIA.cranePad.src}
                jsonLd={[
                    orgJsonLd(),
                    contactPageJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Contact', path: '/contact' },
                    ]),
                    personJsonLd({
                        name: 'Tyler Hachey',
                        jobTitle: 'Director of Sales, North America',
                        telephone: '+1-403-777-2600',
                        email: 'sales@centurionmatting.com',
                        linkedin: 'https://www.linkedin.com/in/tylerhachey/',
                    }),
                    personJsonLd({
                        name: 'Jordan Hickmott',
                        jobTitle: 'Sales Coordinator',
                        email: 'sales@centurionmatting.com',
                        linkedin: 'https://www.linkedin.com/in/jordan-hickmott-b29bb0150/',
                    }),
                ]}
            />

            <HeroPlaceholder
                mediaKey="cranePad"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
                height="h-[360px] md:h-[420px]"
            />

            <section className="mx-auto max-w-7xl grid gap-12 px-6 py-20 md:grid-cols-[1fr_1.4fr] md:px-10 md:py-28 md:gap-16">
                {/* Left - authoritative contact block + Sales Team */}
                <div data-testid="contact-info">
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-brand-gold sm:text-3xl">
                        {SITE.name}
                    </h2>

                    <div className="mt-8 space-y-6">
                        <div data-testid="contact-phone-block">
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-muted">
                                Phone
                            </span>
                            <a
                                href={`tel:${SITE.phone}`}
                                className="mt-1 block font-sans text-base font-semibold tabular-nums text-brand-text transition-colors duration-150 hover:text-brand-gold"
                            >
                                {SITE.phoneDisplay}
                            </a>
                        </div>
                        <div data-testid="contact-email-block">
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-muted">
                                Email
                            </span>
                            <a
                                href={`mailto:${SITE.salesEmail}`}
                                className="mt-1 block break-all font-sans text-base text-brand-text transition-colors duration-150 hover:text-brand-gold"
                            >
                                {SITE.salesEmail}
                            </a>
                        </div>

                        {OFFICES.map((o) => (
                            <div key={o.key} data-testid={`contact-office-${o.key}`}>
                                <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                                    {o.region}
                                </span>
                                <address className="mt-2 not-italic font-sans text-base leading-relaxed text-brand-text">
                                    {o.lines.map((line) => (
                                        <span key={line} className="block">{line}</span>
                                    ))}
                                </address>
                            </div>
                        ))}
                    </div>

                    <hr className="my-10 border-0 border-t border-brand-hairline" />

                    <h3 className="font-display text-xl font-bold uppercase tracking-tight text-brand-text sm:text-2xl">
                        Sales Team
                    </h3>
                    <div className="mt-6 grid gap-px bg-brand-hairline" data-testid="sales-team">
                        {SALES_TEAM.map((p) => (
                            <div
                                key={p.key}
                                data-testid={`sales-card-${p.key}`}
                                className="bg-brand-surface p-5"
                            >
                                <p className="font-sans text-base font-semibold text-brand-text">{p.name}</p>
                                <p className="mt-1 font-sans text-sm text-brand-muted">{p.role}</p>
                                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                                    {p.phone ? (
                                        <a
                                            href={`tel:${p.phone}`}
                                            data-testid={`sales-card-${p.key}-phone`}
                                            className="font-sans text-sm tabular-nums text-brand-gold transition-colors hover:text-brand-gold-hover"
                                        >
                                            {p.phoneDisplay}
                                        </a>
                                    ) : null}
                                    {p.email ? (
                                        <a
                                            href={`mailto:${p.email}`}
                                            data-testid={`sales-card-${p.key}-email`}
                                            data-needs-email={p.needsPersonalEmail || undefined}
                                            className="font-sans text-sm text-brand-gold transition-colors hover:text-brand-gold-hover"
                                        >
                                            {p.email}
                                        </a>
                                    ) : null}
                                    <a
                                        href={p.linkedin || '#'}
                                        target={p.linkedin ? '_blank' : undefined}
                                        rel={p.linkedin ? 'noopener noreferrer' : undefined}
                                        data-testid={`sales-card-${p.key}-linkedin`}
                                        data-needs-url={p.needsLinkedinUrl || undefined}
                                        className="font-sans text-sm text-brand-muted underline underline-offset-4 transition-colors hover:text-brand-text"
                                    >
                                        LinkedIn
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right - form */}
                <div>
                    <span className="eyebrow">Send a Message</span>
                    <h3 className="h-display mt-6 text-2xl sm:text-3xl">
                        Tell us about your project.
                    </h3>

                    {status === 'success' ? (
                        <div
                            data-testid="contact-success"
                            className="mt-10 flex items-start gap-4 border border-brand-gold bg-brand-surface p-6"
                        >
                            <CheckCircle2 className="mt-1 flex-none text-brand-gold" size={20} />
                            <div>
                                <p className="font-display text-base font-bold uppercase tracking-tight text-brand-text">
                                    Message received.
                                </p>
                                <p className="mt-2 font-sans text-sm text-brand-muted">
                                    A Centurion team member will reply within 1 business day.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="mt-10 grid gap-5"
                            noValidate
                        >
                            {/* honeypot */}
                            <input
                                type="text"
                                name="website"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={update('website')}
                                style={{
                                    position: 'absolute',
                                    left: '-10000px',
                                    width: '1px',
                                    height: '1px',
                                }}
                                aria-hidden="true"
                            />

                            <FormRow label="Name" required>
                                <Input
                                    value={form.name}
                                    onChange={update('name')}
                                    required
                                    testId="contact-input-name"
                                />
                            </FormRow>
                            <div className="grid gap-5 md:grid-cols-2">
                                <FormRow label="Company">
                                    <Input
                                        value={form.company}
                                        onChange={update('company')}
                                        testId="contact-input-company"
                                    />
                                </FormRow>
                                <FormRow label="Phone">
                                    <Input
                                        value={form.phone}
                                        onChange={update('phone')}
                                        type="tel"
                                        testId="contact-input-phone"
                                    />
                                </FormRow>
                            </div>
                            <FormRow label="Email" required>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={update('email')}
                                    required
                                    testId="contact-input-email"
                                />
                            </FormRow>
                            <FormRow label="Topic">
                                <Select
                                    value={form.topic}
                                    onChange={update('topic')}
                                    testId="contact-input-topic"
                                >
                                    {TOPICS.map((t) => (
                                        <option key={t.value} value={t.value}>
                                            {t.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormRow>
                            <FormRow label="Message" required>
                                <Textarea
                                    value={form.message}
                                    onChange={update('message')}
                                    required
                                    testId="contact-input-message"
                                />
                            </FormRow>

                            {error ? (
                                <p className="font-sans text-sm text-brand-danger" data-testid="contact-error">
                                    {error}
                                </p>
                            ) : null}

                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={status === 'submitting'}
                                className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647] disabled:opacity-60"
                            >
                                {status === 'submitting' ? (
                                    <>
                                        <Loader2 className="animate-spin" size={14} /> Sending…
                                    </>
                                ) : (
                                    'Send Message'
                                )}
                            </button>
                            <p
                                data-testid="contact-reassurance"
                                className="mt-4 font-sans text-xs leading-relaxed text-brand-muted"
                            >
                                Manufacturer Direct from BC and Alberta -
                                quotes returned within one business day.
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </>
    );
}

const FormRow = ({ label, required, children }) => (
    <label className="block">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
            {required ? <span className="text-brand-gold"> *</span> : null}
        </span>
        <div className="mt-2">{children}</div>
    </label>
);

const Input = ({ testId, ...props }) => (
    <input
        data-testid={testId}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    />
);

const Select = ({ testId, children, ...props }) => (
    <select
        data-testid={testId}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    >
        {children}
    </select>
);

const Textarea = ({ testId, ...props }) => (
    <textarea
        data-testid={testId}
        rows={5}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    />
);
